import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon in react-leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// OpenStreetMap's volunteer tile servers block applications that don't comply
// with their tile usage policy (403 "Access blocked" tiles), so tile requests
// go to CARTO basemaps instead. Both URLs can be pointed at any provider
// (CARTO/MapTiler/Stadia keys, self-hosted) via environment variables.
const TILE_ATTRIBUTION =
  process.env.REACT_APP_MAP_TILE_ATTRIBUTION ||
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions">CARTO</a>';

// CARTO basemaps API key (https://carto.com/basemaps). Key-less URLs still
// work but may be rate-limited; set REACT_APP_CARTO_API_KEY to attach a key.
const CARTO_API_KEY = process.env.REACT_APP_CARTO_API_KEY || '';
const CARTO_KEY_SUFFIX = CARTO_API_KEY ? `?key=${CARTO_API_KEY}` : '';

const TILE_URL_LIGHT =
  process.env.REACT_APP_MAP_TILE_URL_LIGHT ||
  `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png${CARTO_KEY_SUFFIX}`;

const TILE_URL_DARK =
  process.env.REACT_APP_MAP_TILE_URL_DARK ||
  `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png${CARTO_KEY_SUFFIX}`;

// Theme is a `dark` class on <html> (see App.tsx); observe it so the tiles
// follow theme toggles while the map is mounted.
function useIsDarkTheme(): boolean {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setIsDark(root.classList.contains('dark')));
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  onLocationChange: (lat: number, lng: number) => void;
  disabled?: boolean;
}

// Component to handle map clicks
function MapClickHandler({ onLocationSelect }: { onLocationSelect: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  onLocationChange,
  disabled = false
}) => {
  // Default center: Kyiv, Ukraine
  const defaultCenter: [number, number] = [50.4501, 30.5234];

  const [position, setPosition] = useState<[number, number] | null>(
    latitude && longitude ? [latitude, longitude] : null
  );

  const [center, setCenter] = useState<[number, number]>(
    latitude && longitude ? [latitude, longitude] : defaultCenter
  );

  const [addressInput, setAddressInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const isDarkTheme = useIsDarkTheme();

  useEffect(() => {
    if (latitude && longitude) {
      setPosition([latitude, longitude]);
      setCenter([latitude, longitude]);
    }
  }, [latitude, longitude]);

  const handleMapClick = (lat: number, lng: number) => {
    if (disabled) return;

    const newPosition: [number, number] = [lat, lng];
    setPosition(newPosition);
    onLocationChange(lat, lng);
  };

  const handleClearLocation = () => {
    setPosition(null);
    onLocationChange(0, 0);
  };

  const handleSearchAddress = async () => {
    if (!addressInput.trim()) return;

    setIsSearching(true);
    try {
      // Using Nominatim (OpenStreetMap) geocoding API
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressInput)}`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        const newPosition: [number, number] = [lat, lng];

        setPosition(newPosition);
        setCenter(newPosition);
        onLocationChange(lat, lng);
      } else {
        alert('Address not found. Please try a different search.');
      }
    } catch (error) {
      console.error('Error searching address:', error);
      alert('Failed to search address. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="location-picker">
      <div className="mb-2">
        <label className="form-label fw-bold">Location Picker</label>
        <p className="text-muted small mb-2">
          Click on the map to select a location, or search for an address below.
        </p>
      </div>

      {/* Address search */}
      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search address (e.g., Kyiv, Ukraine)"
          value={addressInput}
          onChange={(e) => setAddressInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSearchAddress();
            }
          }}
          disabled={disabled || isSearching}
        />
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleSearchAddress}
          disabled={disabled || isSearching || !addressInput.trim()}
        >
          {isSearching ? (
            'Searching...'
          ) : (
            <>
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              Search
            </>
          )}
        </button>
      </div>

      {/* Current coordinates display */}
      {position && (
        <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
          <div>
            <strong>Selected:</strong> Lat: {position[0].toFixed(6)}, Lng: {position[1].toFixed(6)}
            <br />
            <a
              href={`https://www.google.com/maps?q=${position[0]},${position[1]}`}
              target="_blank"
              rel="noopener noreferrer"
              className="small"
            >
              View on Google Maps
            </a>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={handleClearLocation}
            disabled={disabled}
          >
            Clear
          </button>
        </div>
      )}

      {/* Map */}
      <div style={{ height: '400px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #dee2e6' }}>
        <MapContainer
          center={center}
          zoom={position ? 13 : 6}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution={TILE_ATTRIBUTION}
            url={isDarkTheme ? TILE_URL_DARK : TILE_URL_LIGHT}
          />

          {!disabled && <MapClickHandler onLocationSelect={handleMapClick} />}

          {position && (
            <Marker position={position}>
              <Popup>
                Selected location
                <br />
                Lat: {position[0].toFixed(6)}
                <br />
                Lng: {position[1].toFixed(6)}
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      <p className="text-muted small mt-2 mb-0">
        <i className="fa-solid fa-lightbulb me-1" aria-hidden="true"></i>
        Tip: Use the search box to find a location, or click directly on the map.
      </p>
    </div>
  );
};

export default LocationPicker;
